import React from "react"
import {AxisPlace, isAxisPlace} from "../axis/axis-types"
import {GraphPlace} from "../axis-graph-shared"
import { ICase } from "../../models/data/data-set-types"

export type Point = { x: number, y: number }
export type CPLine = { slope: number, intercept: number, pivot1?: Point, pivot2?: Point }
export const kNullPoint = {x: -999, y: -999}

export const PointDisplayTypes = ["points", "bars"] as const
export type PointDisplayType = typeof PointDisplayTypes[number]

export const isPointDisplayType = (value: string): value is PointDisplayType => {
  return PointDisplayTypes.includes(value as PointDisplayType)
}

export type MarqueeMode = "unclicked" | "selected" | "dragging"

export interface Rect {
  x: number, y: number, width: number, height: number
}

export interface rTreeRect { x: number, y: number, w: number, h: number }

export interface counterProps {
  counter: number,
  setCounter: React.Dispatch<React.SetStateAction<number>>
}

export interface IConnectingLineDescription {
  caseData: ICase
  lineCoords: [number, number]
  plotNum?: number
}

// Minimal shape of a d3-v6-tip instance (the module ships no types — see global.d.ts). A tip is a
// function applied to a selection via `selection.call(tip)`; `show`/`hide` are invoked from hover
// handlers, and `attr`/`html` configure it at construction. Builder methods return the tip for chaining.
export interface ID3Tip {
  (selection: any): void
  show(html: string, target: EventTarget | null): ID3Tip
  hide(): ID3Tip
  attr(name: string, value: string): ID3Tip
  html(accessor: (d: any) => string): ID3Tip
}

export interface ICaseSubsetDescription {
  category?: string
  cellKey: Record<string, string>
}

export const
  transitionDuration = 1000,
  pointRadiusMax = 10,
  pointRadiusMin = 3,
  pointRadiusLogBase = 2.0, // reduce point radius from max by log of (num. cases) base (LogBase).
  pointRadiusSelectionAddend = 1,
  hoverRadiusFactor = 1.5,
  kDataDisplayFont = '12px sans-serif',
  kChoroplethHeight = 16

export const kPortalClass = "portal-parent"
export const kPortalClassSelector = `.${kPortalClass}`

export const PrimaryAttrRoles = ['x', 'y'] as const
export type PrimaryAttrRole = typeof PrimaryAttrRoles[number]
export const GraphTipAttrRoles =
  [...PrimaryAttrRoles, 'rightNumeric', 'topSplit', 'rightSplit', 'legend', 'caption'] as const
export const GraphAttrRoles = [
  ...GraphTipAttrRoles, 'yPlus'] as const
export type GraphAttrRole = typeof GraphAttrRoles[number]
export const GraphSplitAttrRoles = [...PrimaryAttrRoles, 'topSplit', 'rightSplit'] as const
export type GraphSplitAttrRole = typeof GraphSplitAttrRoles[number]

export const MapAttrRoles = ['lat', 'long', 'pinLat', 'pinLong', 'polygon'] as const
export type MapAttrRole = typeof MapAttrRoles[number]

export const AttrRoles = [...GraphAttrRoles, ...MapAttrRoles] as const
export type AttrRole = typeof AttrRoles[number]
// We leave open the possibility that TipAttrRoles may include some that are not GraphTipAttrRoles
export const TipAttrRoles = [...GraphTipAttrRoles] as const

export const attrRoleToAxisPlace: Partial<Record<GraphAttrRole, AxisPlace>> = {
  x: "bottom",
  y: "left",
  rightNumeric: "rightNumeric",
  rightSplit: "rightCat",
  topSplit: "top"
}
export const attrRoleToGraphPlace: Partial<Record<GraphAttrRole, GraphPlace>> = {
  ...attrRoleToAxisPlace,
  yPlus: "yPlus",
  legend: "legend"
}

export const axisPlaceToAttrRole: Record<AxisPlace, GraphAttrRole> = {
  bottom: "x",
  left: "y",
  // The "leftLower" axis has no owning attribute — it is driven by an adornment (e.g. Residual Plot).
  // Map it to "y" so that any residual code paths that read the role for it see a sensible fallback.
  leftLower: "y",
  top: "topSplit",
  rightCat: "rightSplit",
  rightNumeric: "rightNumeric"
}

// Per-axis-place behavioral traits, consolidating decisions that clients would otherwise make with
// scattered `place === 'leftLower'` checks. Today only "leftLower" (the Residual Plot's
// adornment-owned, non-interactive lower y-axis) diverges from the default. This table is a
// stepping stone toward the axes owning these properties themselves (cf. PlotModel's property
// getters); when that happens these rows migrate onto the axis model and getAxisPlaceTraits(place)
// becomes axis.traits. Purely place-intrinsic geometry (orientation, render side, length) stays in
// its existing per-place tables and is intentionally not represented here.
export interface IAxisPlaceTraits {
  isAdornmentOwned: boolean   // no owning attribute; an adornment manages its scale/domain
  isInteractive: boolean      // supports drag rects, a drop zone, and an attribute-assignment menu
  fixedLabelKey?: string      // fixed translation-key label instead of an attribute-derived name
  alwaysShowsZeroLine: boolean // unconditionally draws a reference line at 0 (not gated on plot state)
}

// The default row: an ordinary attribute-owned, interactive axis with no fixed label or forced
// zero line. Also returned for non-axis GraphPlaces (legend/plot/yPlus), which are never axes.
const kDefaultAxisPlaceTraits: IAxisPlaceTraits =
  { isAdornmentOwned: false, isInteractive: true, alwaysShowsZeroLine: false }

const axisPlaceTraits: Record<AxisPlace, IAxisPlaceTraits> = {
  bottom:       kDefaultAxisPlaceTraits,
  left:         kDefaultAxisPlaceTraits,
  leftLower:    { isAdornmentOwned: true,  isInteractive: false, alwaysShowsZeroLine: true,
                  fixedLabelKey: "V3.ResidualPlot.axisLabel" },
  top:          kDefaultAxisPlaceTraits,
  rightCat:     kDefaultAxisPlaceTraits,
  rightNumeric: kDefaultAxisPlaceTraits
}

// Accepts any GraphPlace so callers that carry the wider type (e.g. axis labels) need no narrowing;
// non-axis places fall back to the default traits.
export const getAxisPlaceTraits = (place: GraphPlace): IAxisPlaceTraits =>
  isAxisPlace(place) ? axisPlaceTraits[place] : kDefaultAxisPlaceTraits

export const graphPlaceToAttrRole: Record<GraphPlace, GraphAttrRole> = {
  ...axisPlaceToAttrRole,
  legend: "legend",
  plot: "legend",
  yPlus: "yPlus"
}

export const kOther = '__other__' // Used as key to category map entry that stashes overflow of what
                                              // doesn't fit on cell axis. Not likely to be an attribute value
export const kMain = '__main__' // When an axis has no categories, this is its pseudo-category
